// src/features/study/views/GuidelineArtifactView.js
import React from "react";
import { createPortal } from "react-dom";
import PlanArtifactContent from "../../../components/artifacts/PlanArtifactContent";
import EditHistoryModal from "../../../components/artifacts/EditHistoryModal";
import ArtifactDiffModal from "../../../components/artifacts/ArtifactDiffModal";
import { modifyArtifactText } from "../../../services/artifactAIService";
import { apiFetch, alertUnlessQuotaExceeded } from "../../../services/apiClient";
import { markdownToHtml } from "../../../utils/artifactUtils";
import { listArtifactEditHistory, createArtifactEditHistory } from "../../../services/artifactHistoryService";
import GuidelineTopToolbar from "../components/GuidelineTopToolbar";
import "./GuidelineArtifactView.scss";

export default function GuidelineArtifactView({
  artifact,
  studyId,
  studyName,
  isEditing,
  isGenerating,
  editor,
  onSave,
  onCancelEdit,
  onCopy,
  onDownloadWord,
  onDownloadPDF,
  onDownloadMarkdown,
  onDelete,
  onArtifactUpdate,
  onDirectEdit,
}) {
  // 고정 툴바 표시 여부
  const [showFixedToolbar, setShowFixedToolbar] = React.useState(false);
  const [sidebarWidth, setSidebarWidth] = React.useState(250);
  const toolbarRef = React.useRef(null);

  // AI 재작성/히스토리 상태 (Plan과 동일 UX)
  const [rewriteOpen, setRewriteOpen] = React.useState(false);
  const [rewritePrompt, setRewritePrompt] = React.useState("");
  const [rewriteLoading, setRewriteLoading] = React.useState(false);
  const [rewriteDiffOpen, setRewriteDiffOpen] = React.useState(false);
  const [rewriteDiffData, setRewriteDiffData] = React.useState({ original: "", modified: "" });
  const [rewriteProcessing, setRewriteProcessing] = React.useState(false);

  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [editHistory, setEditHistory] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  // 사이드바 너비 계산 함수
  const updateSidebarWidth = React.useCallback(() => {
    const sidebar = document.querySelector(".common-sidebar") || document.querySelector(".b2b-sidebar");
    if (sidebar) {
      const isCollapsed = sidebar.classList.contains("collapsed");
      setSidebarWidth(isCollapsed ? 60 : 250);
    }
  }, []);

  // 스크롤 이벤트로 툴바가 화면에서 사라지는지 감지
  React.useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    const handleScroll = () => {
      // 툴바의 하단이 화면 상단(0)보다 위로 올라가면 fixed 표시
      const toolbarBottom = toolbar.getBoundingClientRect().bottom;
      const shouldShow = toolbarBottom < 0;
      setShowFixedToolbar(shouldShow);
      updateSidebarWidth();
    };

    // 어떤 요소에서 스크롤하든 감지
    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // 초기 체크

    return () => {
      document.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("scroll", handleScroll);
    };
  }, [updateSidebarWidth]);

  // 사이드바 접힘/펼침 상태 변화 감지
  React.useEffect(() => {
    const sidebar = document.querySelector(".common-sidebar") || document.querySelector(".b2b-sidebar");
    if (!sidebar) return;

    const observer = new MutationObserver(() => {
      updateSidebarWidth();
    });

    observer.observe(sidebar, { attributes: true, attributeFilter: ["class"] });
    updateSidebarWidth(); // 초기 체크

    return () => observer.disconnect();
  }, [updateSidebarWidth]);

  const refreshHistory = React.useCallback(async () => {
    if (!artifact?.id) return;
    setHistoryLoading(true);
    try {
      const list = await listArtifactEditHistory(artifact.id, { limit: 50 });
      const mapped = (list || []).map((h) => ({
        id: h.id,
        prompt: h.prompt || "",
        originalText: h.before_markdown || "",
        modifiedMarkdown: h.after_markdown || "",
        from: h.selection_from ?? null,
        to: h.selection_to ?? null,
        status: "applied",
        createdAt: h.created_at || null,
      }));
      setEditHistory(mapped);
    } catch (e) {
      setEditHistory([]);
      alert(`히스토리를 불러오지 못했습니다: ${e.message}`);
    } finally {
      setHistoryLoading(false);
    }
  }, [artifact?.id]);

  const handleHistoryOpen = React.useCallback(async () => {
    setHistoryOpen(true);
    await refreshHistory();
  }, [refreshHistory]);

  const handleRewriteSubmit = React.useCallback(async () => {
    if (!rewritePrompt.trim() || rewriteLoading) return;
    setRewriteLoading(true);
    try {
      const full = artifact?.content || "";
      const result = await modifyArtifactText(artifact.id, full, rewritePrompt.trim(), full, {
        selected_markdown_hint: full,
      });
      if (!result?.success) throw new Error(result?.error || "재작성에 실패했습니다.");
      if (!result.modified) throw new Error("AI 결과가 비어있습니다.");

      let modifiedText = result.modified;
      try {
        const trimmed = modifiedText.trim();
        if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === "object" && parsed.modified) {
            modifiedText = parsed.modified;
          } else if (parsed && typeof parsed === "object" && parsed.original) {
            modifiedText = parsed.original;
          }
        }
      } catch (e) {
        console.warn("JSON 파싱 실패, 원본 텍스트 사용:", e);
      }

      setRewriteDiffData({ original: full, modified: modifiedText, prompt: rewritePrompt.trim() });
      setRewriteDiffOpen(true);
      setRewriteOpen(false);
      setRewritePrompt("");
    } catch (e) {
      alertUnlessQuotaExceeded(e, e.message || String(e));
    } finally {
      setRewriteLoading(false);
    }
  }, [artifact?.content, artifact?.id, rewriteLoading, rewritePrompt]);

  // 툴바 props (원래 툴바와 fixed 툴바에서 공유)
  const toolbarProps = {
    isEditing,
    isGenerating,
    onRewrite: () => setRewriteOpen((v) => !v),
    onHistory: handleHistoryOpen,
    onSave,
    onCancelEdit,
    onCopy,
    onDownloadWord,
    onDownloadPDF,
    onDownloadMarkdown,
    onDelete,
    rewriteOpen: rewriteOpen && !isEditing,
    rewritePrompt,
    onRewritePromptChange: setRewritePrompt,
    onRewriteSubmit: handleRewriteSubmit,
    rewriteLoading,
    onRewriteClose: () => {
      setRewriteOpen(false);
      setRewritePrompt("");
    },
  };

  return (
    <>
      {/* 원래 툴바: 문서 흐름에 따라 위치 */}
      <div ref={toolbarRef}>
        <GuidelineTopToolbar {...toolbarProps} />
      </div>

      {/* Fixed 툴바: 원래 툴바가 화면에서 사라지면 상단에 고정 표시 */}
      {showFixedToolbar &&
        createPortal(
          <div className="guideline-fixed-toolbar-container" style={{ left: sidebarWidth }}>
            <GuidelineTopToolbar {...toolbarProps} />
          </div>,
          document.body
        )}

      {/* AI 재작성 결과 비교 모달 */}
      <ArtifactDiffModal
        open={rewriteDiffOpen}
        data={rewriteDiffData}
        isProcessing={rewriteProcessing}
        onClose={() => {
          if (rewriteProcessing) return;
          setRewriteDiffOpen(false);
          setRewriteDiffData({ original: "", modified: "" });
        }}
        onApply={async () => {
          const md = rewriteDiffData?.modified || "";
          if (!md) return;
          const original = (rewriteDiffData?.original || "").trim();
          const promptUsed = rewriteDiffData?.prompt ?? rewritePrompt ?? "";

          // 안전장치: 전체 문서가 일부 텍스트로 "잘려" 적용되는 사고 방지
          if (original && original.length > 800 && md.length < original.length * 0.7) {
            alert(
              "AI 결과가 문서 전체가 아니라 일부만 포함된 것 같습니다.\n\n" +
                "이대로 적용하면 내용이 사라질 수 있어 적용을 중단했습니다.\n" +
                "다시 생성해 주세요. (문서 전체를 반환하도록 개선 중)"
            );
            return;
          }
          setRewriteProcessing(true);
          try {
            const resp = await apiFetch(`/artifacts/${artifact.id}`, {
              method: "PUT",
              body: JSON.stringify({ content: md }),
            });
            if (!resp.ok) {
              const err = await resp.json().catch(() => ({ error: "저장 실패" }));
              throw new Error(err.error || "저장 실패");
            }
            if (editor) {
              const html = markdownToHtml(md);
              editor.commands.setContent(html);
              editor.setEditable(false);
            }
            onArtifactUpdate?.(md);

            // AI 재작성(툴바) 적용도 수정 히스토리에 기록
            if (original && artifact?.id) {
              try {
                await createArtifactEditHistory(artifact.id, {
                  prompt: promptUsed || "",
                  source: "rewrite",
                  before_markdown: original,
                  after_markdown: md,
                  selection_from: null,
                  selection_to: null,
                });
              } catch (e) {
                console.warn("[edit_history] rewrite save failed:", e);
              }
            }

            setRewriteDiffOpen(false);
            setRewriteDiffData({ original: "", modified: "" });
            setRewritePrompt("");
          } catch (e) {
            alertUnlessQuotaExceeded(e, e.message || String(e));
          } finally {
            setRewriteProcessing(false);
          }
        }}
      />

      {/* 본문 (드래그 수정/저장 히스토리 포함: Plan과 동일 컴포넌트 재사용) */}
      <div style={{ marginTop: "32px" }} className="guideline-markdown">
        <PlanArtifactContent
          artifact={artifact}
          isEditing={isEditing}
          isGenerating={isGenerating}
          studyId={studyId}
          studyName={studyName}
          editor={editor}
          onArtifactUpdate={onArtifactUpdate}
          onDirectEdit={onDirectEdit}
          onHistoryUpdate={setEditHistory}
          dragHintStorageKey="guideline_artifact_drag_hint_seen"
        />
      </div>

      {/* 수정 히스토리 모달 */}
      <EditHistoryModal
        open={historyOpen}
        history={editHistory}
        loading={historyLoading}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
}
